import { ensureRouteData } from '@/app/query-client'
import { brokersByStockQueryOptions } from '@/features/brokers/api/queries'
import { rememberLastBrokerStock } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { byStockSearchSchema, toByStockRange } from '@/screens/brokers/search'

/**
 * The `/brokers/by-stock/$symbol` loader.
 *
 * MAIN, but keyed on the `period` search param, or the custom `start`/`end`
 * (`byStockSearchSchema`), so this awaits only the ENTRY URL's window — a chip
 * click or a calendar pick afterwards is a search-only change that reruns no
 * loader, and `ByStockScreen` keeps reading it with `useBrokersByStock`'s own
 * `placeholderData: keepPreviousData`, not a suspense hook (docs/route-loading.md,
 * "Search params"). No secondary read: the only other thing on this page is a
 * plain link to PV range.
 *
 * `STOCK_NOT_FOUND` resolves to `null` rather than throwing
 * (docs/route-loading.md, "Not-found answers" — see
 * `brokersByStockQueryOptions`'s own comment), so this loader settles
 * normally even for a bad symbol, and `ByStockScreen`
 * keeps its own `data === null` branch — the same shape Stock's
 * `company === null` uses.
 *
 * Remembers the symbol as last-used ONLY for a real result — a bad symbol,
 * or a visit whose read never resolved, must not overwrite a good remembered
 * stock — and ONLY for a real navigation (`!preload`): the router runs this
 * loader on a hover too, and a cursor merely crossing a stock link must not
 * overwrite what the member last actually opened. PV range's own loader
 * writes the same key: the two screens share one "last broker-analysis
 * stock" (`lastUsed.ts`).
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/`, `lib/` and
 * schema modules only — route loaders are not code-split, so anything
 * imported here ships in the entry chunk.
 */
export async function loadByStock({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = byStockSearchSchema.parse(location.search)
  const result = await ensureRouteData(
    queryClient,
    brokersByStockQueryOptions(params.symbol, search.period, toByStockRange(search)),
  )
  if (!preload && result !== null) rememberLastBrokerStock(params.symbol)
}
